import { ReactNode, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { IDiscardQuestion, useModalCloseGuard } from '../../hooks/useModalCloseGuard';
import { useAreaSource } from '../../hooks/useUnsavedEditSource';
import { IUnsavedEditSource, pageUnloadConfirmed, UnsavedEditArea } from '../../lib/UnsavedEditArea';
import { UnsavedEditContext } from '../../context/UnsavedEditContext';
import { useUi } from '../../provider/UiContext';
import {
    addNavigationGuard,
    BlockedNavigation,
    bypassNextNavigation,
    INavigationGuard,
    routerState,
    toRouterPath,
} from '../../lib/navigationGuard';

const NO_SOURCE: IUnsavedEditSource = { hasUnsavedInput: () => false };

const pathChanged = (from: URL, to: URL) => from.pathname !== to.pathname;

export interface IUnsavedEditScopeProps {
    children?: ReactNode;
    /**
     * Корень приложения: пока какая-то правка в режиме правки, уход со страницы (меню, ссылка, «Назад») ждёт ответа
     * на вопрос, а закрытие и перезагрузка вкладки браузера при несохранённом предупреждают. Переход после ответа —
     * `navigate` из `UiProvider` (путь — без его `basename`).
     */
    guardNavigation?: boolean;
    /**
     * Какой переход между адресами — уход со страницы. По умолчанию — смена пути: параметры и якорь меняет сама
     * страница (фильтры, `?open=`, вкладки) и свою правку бережёт сама.
     */
    leaves?: INavigationGuard['leaves'];
    /**
     * Правка самой области — когда её владелец рисует область у себя внутри (тема и хост порталов окна вопроса —
     * области, а не страницы).
     */
    source?: IUnsavedEditSource;
    /** Правка `source` в режиме правки. */
    editing?: boolean;
}

/**
 * Область правок: правки внутри (`useUnsavedEditSource`) спрашивают «Закрыть без сохранения?» перед уходом с них
 * (`useConfirmDiscard`), вопрос — в этой области. Вложенная область — окно со своим списком и карточкой: смена строки
 * в нём спрашивает только о его правке.
 */
const UnsavedEditScope = ({
    children,
    guardNavigation = false,
    leaves,
    source,
    editing = true,
}: IUnsavedEditScopeProps) => {
    const parent = useContext(UnsavedEditContext);
    const answerRef = useRef<(discard: boolean) => void>(undefined);
    const [question, setQuestion] = useState<Partial<IDiscardQuestion>>();
    const settle = (discard: boolean) => {
        const answer = answerRef.current;
        answerRef.current = undefined;
        answer?.(discard);
    };
    const dialog = useModalCloseGuard({
        onClose: () => settle(true),
        hasUnsavedInput: () => true,
        onKeep: () => settle(false),
        question,
    });
    const confirmClose = useLatest(dialog.confirmClose);
    const [area] = useState(
        () =>
            new UnsavedEditArea(parent, (answer, asked) => {
                // новый вопрос заменяет прежний: тот уход не состоялся
                answerRef.current?.(false);
                answerRef.current = answer;
                setQuestion(asked);
                confirmClose.current();
            }),
    );
    useAreaSource(source ? area : null, source ?? NO_SOURCE, editing);

    const { navigate, basename } = useUi();
    const router = useLatest({ navigate, basename, leaves });
    const anyEditing = useSyncExternalStore(area.subscribe, area.isEditing);

    useEffect(() => {
        if (!guardNavigation || !anyEditing) return;
        const leave = (navigation: BlockedNavigation) => {
            bypassNextNavigation();
            // шаг истории роутер BrowserRouter узнаёт из popstate, как свой navigate(-1)
            if (navigation.kind === 'delta') window.history.go(navigation.delta);
            else
                void router.current.navigate(toRouterPath(navigation.url, router.current.basename ?? ''), {
                    replace: navigation.replace,
                    state: routerState(navigation.state),
                });
        };
        const removeGuard = addNavigationGuard({
            shouldBlock: area.blocksLeave,
            leaves: (from, to) => (router.current.leaves ?? pathChanged)(from, to),
            onBlock: (navigation) => area.confirmLeave((allowed) => allowed && leave(navigation)),
        });
        const warnOnUnload = (event: BeforeUnloadEvent) => {
            if (pageUnloadConfirmed() || !area.losesOnUnload()) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnOnUnload);
        return () => {
            removeGuard();
            window.removeEventListener('beforeunload', warnOnUnload);
        };
    }, [guardNavigation, anyEditing, area, router]);

    return (
        <UnsavedEditContext value={area}>
            {children}
            {dialog.confirmDialog}
        </UnsavedEditContext>
    );
};

export default UnsavedEditScope;
